import { Shipping } from 'src/types';
import {
  FilterType,
  FilterUIPlacementEnum,
  IFilter,
  FilterOffer,
  FilterSelection,
  FilterUISubPlacementEnum,
} from './types';

const isOfferWithShippingInfoAndSelections = (
  offer: FilterOffer,
  selection?: FilterSelection
) => {
  return (
    offer &&
    'shipping' in offer &&
    offer.shipping?.nodes &&
    selection &&
    selection.length > 0
  );
};

export const isOfferWithMissingShippingInfo = (offer: FilterOffer) => {
  return (
    !(offer && 'shipping' in offer) ||
    !offer.shipping?.nodes ||
    !(offer.shipping?.nodes.length > 0)
  );
};

export const getFiltersConfig = (): IFilter[] => {
  return [
    {
      label: 'sort.price_includes_shipping',
      type: FilterType.PRICES_INC_SHIPPING,
      placement: FilterUIPlacementEnum.OFFERLIST_HEADER,
      filterFn: (offer: FilterOffer) => {
        return Boolean(offer?.price?.inclShipping);
      },
      missingInfoFn: (offer: FilterOffer) => {
        return !offer?.price?.inclShipping;
      },
      filterAlternativeOffers: true,
      active: false,
      treatUnfeaturedAsMissingInfo: true,
    },
    {
      label: 'stock-status.in_stock',
      type: FilterType.IN_STOCK,
      placement: FilterUIPlacementEnum.FILTER_MENU,
      subPlacement: FilterUISubPlacementEnum.SHOW_ONLY,
      filterFn: (offer: FilterOffer) => {
        return Boolean(offer?.stock?.status === 'in_stock');
      },
      missingInfoFn: (offer: FilterOffer) => {
        return Boolean(
          !offer?.stock?.status || offer?.stock?.status === 'unknown'
        );
      },
      filterAlternativeOffers: true,
      active: false,
      treatUnfeaturedAsMissingInfo: false,
    },
    {
      label: 'price-filter.featured-shops',
      type: FilterType.FEATURED_SHOP,
      placement: FilterUIPlacementEnum.FILTER_MENU,
      subPlacement: FilterUISubPlacementEnum.SHOW_ONLY,
      filterFn: (offer: FilterOffer) => {
        return Boolean(offer && 'store' in offer && offer.store?.featured);
      },
      filterAlternativeOffers: false,
      active: false,
    },
    {
      label: 'price-filter.authorized-dealer',
      type: FilterType.AUTHORIZED_DEALER,
      placement: FilterUIPlacementEnum.FILTER_MENU,
      subPlacement: FilterUISubPlacementEnum.SHOW_ONLY,
      filterFn: (offer: FilterOffer) => {
        return Boolean(
          offer && 'authorizedDealer' in offer && offer.authorizedDealer
        );
      },
      filterAlternativeOffers: false,
      active: false,
    },
    {
      label: 'price-filter.international',
      type: FilterType.INTERNATIONAL,
      placement: FilterUIPlacementEnum.FILTER_MENU,
      subPlacement: FilterUISubPlacementEnum.ADDITIVE,
      filterFn: (offer: FilterOffer) => {
        return Boolean(offer?.primaryMarket);
      },
      filterAlternativeOffers: true,
      active: true,
      additive: true,
    },
    {
      label: 'price-filter.not-for-sale',
      type: FilterType.NOT_FOR_SALE,
      placement: FilterUIPlacementEnum.FILTER_MENU,
      subPlacement: FilterUISubPlacementEnum.ADDITIVE,
      filterFn: (offer: FilterOffer) => {
        return Boolean(
          offer && offer?.availability?.condition !== 'NotAvailableForOrder'
        );
      },
      filterAlternativeOffers: true,
      active: true,
      additive: true,
    },
    {
      label: '',
      type: FilterType.VARIANT_SIZE,
      placement: FilterUIPlacementEnum.PRODUCT_HEADER,
      filterFn: (offer: FilterOffer, selection?: FilterSelection) => {
        return Boolean(
          offer &&
            offer?.variantInfo?.size &&
            selection?.includes(offer.variantInfo.size)
        );
      },
      missingInfoFn: (offer: FilterOffer) => {
        return !offer?.variantInfo?.size;
      },
      filterAlternativeOffers: true,
      active: false,
      selection: [],
      initialSelection: [],
    },
    {
      label: '',
      type: FilterType.VARIANT_COLOR,
      placement: FilterUIPlacementEnum.PRODUCT_HEADER,
      filterFn: (offer: FilterOffer, selection?: FilterSelection) => {
        return Boolean(
          selection?.length &&
            offer?.variantInfo?.colors?.some(color => selection.includes(color))
        );
      },
      missingInfoFn: (offer: FilterOffer) => {
        return (
          !offer?.variantInfo?.colors || offer?.variantInfo?.colors.length < 1
        );
      },
      filterAlternativeOffers: true,
      active: false,
      selection: [],
      initialSelection: [],
    },
    {
      label: '',
      type: FilterType.CONDITION,
      placement: FilterUIPlacementEnum.PRODUCT_HEADER,
      filterFn: (offer: FilterOffer, selection?: FilterSelection) => {
        return Boolean(
          offer?.condition && selection?.includes(offer?.condition)
        );
      },
      filterAlternativeOffers: true,
      active: true,
      selection: ['new'],
      initialSelection: ['new'],
    },
    {
      label: '',
      type: FilterType.DELIVERY_METHOD,
      placement: FilterUIPlacementEnum.FILTER_MENU,
      filterFn: (offer: FilterOffer, selection?: FilterSelection) => {
        return Boolean(
          isOfferWithShippingInfoAndSelections(offer, selection) &&
            offer!.shipping?.nodes.some(
              node =>
                node?.deliveryMethod && selection?.includes(node.deliveryMethod)
            )
        );
      },
      filterShippingFn: (
        shipping: Shipping,
        selection?: FilterSelection
      ): boolean =>
        Boolean(
          selection &&
            shipping.deliveryMethod &&
            selection.includes(shipping.deliveryMethod)
        ),
      missingInfoFn: isOfferWithMissingShippingInfo,
      filterAlternativeOffers: true,
      active: false,
      selection: [],
    },
    {
      label: '',
      type: FilterType.CARRIERS,
      placement: FilterUIPlacementEnum.FILTER_MENU,
      filterFn: (offer: FilterOffer, selection?: FilterSelection) => {
        return Boolean(
          isOfferWithShippingInfoAndSelections(offer, selection) &&
            offer!.shipping?.nodes.some(
              node => node?.carrier && selection?.includes(node.carrier)
            )
        );
      },
      filterShippingFn: (
        shipping: Shipping,
        selection?: FilterSelection
      ): boolean =>
        Boolean(
          selection && shipping.carrier && selection.includes(shipping.carrier)
        ),
      missingInfoFn: isOfferWithMissingShippingInfo,
      filterAlternativeOffers: true,
      active: false,
      selection: [],
    },
  ];
};
